// src/pages/MPIDashboardPage.js
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mentoringApi as axios, authApi } from '../axiosInstance';
import D1ProductivityDrillDown from '../components/mpi/D1ProductivityDrillDown';
import D2RampupDrillDown from '../components/mpi/D2RampupDrillDown';
import D3RiskDrillDown from '../components/mpi/D3RiskDrillDown';
import D4LeadershipDrillDown from '../components/mpi/D4LeadershipDrillDown';
import MpiTrendModal from '../components/mpi/MpiTrendModal';
import DataSourceBar from '../components/mpi/DataSourceBar';
import CentralGauge, { dimColor } from '../components/mpi/CentralGauge';
import { ensureMentoringSession, getCachedWhoami } from '../lib/session';

/* ─── Constants ─────────────────────────────────────────────────── */
const DEV = process.env.NODE_ENV !== 'production';

/* ─── dimColor, useCountUp, CentralGauge ──────────────────────────
 * Ausgelagert nach src/components/mpi/CentralGauge.js (P17-S9g).
 * Import oben: import CentralGauge, { dimColor } from '…/CentralGauge';
 * ────────────────────────────────────────────────────────────────── */

/* ─── DimCard ────────────────────────────────────────────────────── */
function DimCard({ dim, onClick, active }) {
  const isNull = dim.score == null;
  const col = dimColor(dim.score);
  const r = 26;
  const circ = 2 * Math.PI * r;
  const dash = isNull ? 0 : circ * (dim.score / 100);

  const sc = {
    ALERT:   { bg: '#EF444412', border: '#EF444440', text: '#EF4444' },
    OK:      { bg: '#10B98112', border: '#10B98140', text: '#10B981' },
    PARTIAL: { bg: '#F59E0B12', border: '#F59E0B40', text: '#F59E0B' },
    MISSING: { bg: '#47556912', border: '#47556940', text: '#64748B' },
  }[dim.status];

  return (
    <div onClick={() => onClick(dim)} style={{
      background: active ? `linear-gradient(135deg, ${col}18, ${col}06)` : 'rgba(255,255,255,0.03)',
      border: `1px solid ${active ? col + '55' : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 18, padding: '20px', cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: active ? `0 8px 32px ${col}1A` : 'none',
      height: '100%',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.15em', marginBottom: 3 }}>
            {dim.weight != null ? `${dim.id} · ${dim.weight}%` : dim.id}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.88)', lineHeight: 1.45 }}>
            {dim.label}<br />{dim.sublabel}
          </div>
        </div>
        <div style={{
          background: sc.bg, border: `1px solid ${sc.border}`,
          borderRadius: 20, padding: '2px 8px',
          fontSize: 9, color: sc.text, letterSpacing: '0.06em', whiteSpace: 'nowrap',
        }}>{dim.statusLabel}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <svg width={64} height={64} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
          <circle cx={32} cy={32} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={6} />
          {!isNull && (
            <circle cx={32} cy={32} r={r} fill="none" stroke={col} strokeWidth={6}
              strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 5px ${col}88)` }}
            />
          )}
        </svg>
        <div>
          <div style={{
            fontSize: 32, fontWeight: 800, lineHeight: 1, letterSpacing: '-1.5px',
            color: isNull ? '#475569' : col,
            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
          }}>
            {isNull ? '—' : dim.score.toFixed(0)}
          </div>
          <div style={{ fontSize: 9, color: dim.d2FromSurvey ? 'rgba(251,191,36,0.6)' : 'rgba(255,255,255,0.2)', marginTop: 3 }}>
            {isNull ? 'keine Daten' : dim.d2FromSurvey ? 'Aus Kulturumfrage' : ''}
          </div>
        </div>
      </div>
      {dim.coverage_badge && (
        <div style={{
          fontSize: '0.65rem',
          marginTop: 8,
          marginBottom: 4,
          padding: '2px 8px',
          borderRadius: 10,
          backgroundColor: dim.coverage_status === 'GREEN' ? 'rgba(34,197,94,0.15)' : dim.coverage_status === 'YELLOW' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
          color: dim.coverage_status === 'GREEN' ? '#22C55E' : dim.coverage_status === 'YELLOW' ? '#F59E0B' : '#EF4444',
          display: 'inline-block',
        }}>
          {dim.coverage_badge}
        </div>
      )}
    </div>
  );
}

/* ─── DetailDrawer ───────────────────────────────────────────────── */
function DetailDrawer({ dim, onClose }) {
  const col = dimColor(dim.score);
  return (
    <div style={{
      background: `linear-gradient(135deg, ${col}0E, rgba(8,11,18,0.95))`,
      backdropFilter: 'blur(24px)',
      border: `1px solid ${col}30`,
      borderRadius: 18, padding: '22px 26px',
      animation: 'mpiSlideDown 0.18s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `${col}20`, border: `1px solid ${col}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17, color: col,
          }}>{dim.icon}</div>
          <div>
            <div style={{ fontSize: 9, color: col, letterSpacing: '0.2em', textTransform: 'uppercase' }}>{dim.id} Detail</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>{dim.label} {dim.sublabel}</div>
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, color: 'rgba(255,255,255,0.45)', cursor: 'pointer',
          padding: '6px 12px', fontSize: 11,
        }}>✕ Schließen</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        {dim.details.map((d, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 12, padding: '14px',
          }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', marginBottom: 7 }}>{d.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: d.value != null ? col : '#475569', letterSpacing: '-1px' }}>
              {d.value != null ? `${d.value}${d.unit}` : '—'}
            </div>
          </div>
        ))}
      </div>
      <div style={{
        padding: '10px 14px', background: 'rgba(255,255,255,0.03)',
        borderRadius: 9, borderLeft: `3px solid ${col}`,
        fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7,
      }}>{dim.note}</div>
    </div>
  );
}

/* ─── Helpers: API → Dimensions ─────────────────────────────────── */
const DIMENSION_WEIGHTS = {
  'FULL':       { d1: 40,   d2: 20,   d3: 20,   d4: 20   },
  'S1_S2':      { d1: 40,   d2: 15,   d3: 20,   d4: 25   },
  'S1_ONLY':    { d1: null, d2: 15,   d3: 25,   d4: 60   },
  'D4_ONLY':    { d1: null, d2: null, d3: 30,   d4: 70   },
  'D1_MISSING': { d1: null, d2: 25,   d3: 37.5, d4: 37.5 },
};

// Optische Reihenfolge der Datenquellen-Kacheln (rein Anzeige; key/active/tooltip
// wandern als Objekt mit). Rang per Label-Substring, key-agnostisch; unbekannte
// Segmente stabil ans Ende. Beruehrt KEINE Aktiv-Logik/Schwellen/Backend.
const DATASOURCE_LABEL_RANK = [
  /kultur|umfrage/i,         // 1 Kulturumfrage
  /crm/i,                    // 2 CRM-Kennzahlen
  /check.?in/i,              // 3 Check-ins
  /milestone|fk-/i,          // 4 FK-Milestones
  /mentor|verst(ä|ae)rk/i,   // 5 Mentoring-Verstaerker
];
function orderDatasourceSegments(segments) {
  if (!Array.isArray(segments)) return segments;
  const rank = (seg) => {
    const s = String(seg?.name || seg?.label || seg?.id || seg?.key || '');
    const i = DATASOURCE_LABEL_RANK.findIndex(rx => rx.test(s));
    return i === -1 ? DATASOURCE_LABEL_RANK.length : i;
  };
  return segments
    .map((seg, i) => ({ seg, i }))
    .sort((a, b) => (rank(a.seg) - rank(b.seg)) || (a.i - b.i))
    .map(x => x.seg);
}

function buildDimensions(scores, alerts, leadershipSurvey, coverage, tenant) {
  const activeAlertCount = (alerts || []).filter(a => a.alert_active).length;
  const w = DIMENSION_WEIGHTS[scores?.weighting_mode] || DIMENSION_WEIGHTS['FULL'];

  // D4: prefer survey score, fall back to legacy
  const surveyAvailable = leadershipSurvey?.survey_available === true;
  const d4Score = surveyAvailable
    ? (leadershipSurvey.d4_survey_score != null ? parseFloat(leadershipSurvey.d4_survey_score) : null)
    : (scores.d4_leadership ?? null);
  const d4Respondents = leadershipSurvey?.respondent_count ?? 0;

  // D4 status
  let d4Status, d4StatusLabel;
  if (!surveyAvailable && !scores.d4_leadership) {
    d4Status = 'MISSING';
    d4StatusLabel = 'Zu wenig Antworten';
  } else if (alerts?.some(a => a.alert_active)) {
    d4Status = 'ALERT';
    d4StatusLabel = `${alerts.filter(a => a.alert_active).length} Alerts`;
  } else {
    d4Status = 'OK';
    d4StatusLabel = 'Stabil';
  }

  // Bug D: D3-Status nicht mehr hart 'Stabil'. Bei fehlenden Daten 'Keine Daten'.
  // Greift nur bei null-Score, D4_ONLY oder (im Survey-Kontext) <5 Antworten —
  // Mentoring-NLs (coverage.type !== 'survey', FULL/S1_S2) bleiben unveraendert.
  const d3Score = scores.d3_retention != null && scores.d3_risk != null
    ? (scores.d3_retention + scores.d3_risk) / 2
    : scores.d3_retention ?? scores.d3_risk ?? null;
  const d3SurveyContext = coverage?.type === 'survey';
  const d3RespondentCount = leadershipSurvey?.respondent_count ?? null;
  const d3NoData = d3Score == null
    || scores?.weighting_mode === 'D4_ONLY'
    || (d3SurveyContext && d3RespondentCount != null && d3RespondentCount < 5);
  const d3Status = d3NoData ? 'MISSING' : 'OK';
  const d3StatusLabel = d3NoData ? 'Keine Daten' : 'Stabil';

  return [
    {
      id: 'D1', label: 'Produktivität', sublabel: '& Qualität',
      score: scores.d1_productivity != null && scores.d1_quality != null
        ? (scores.d1_productivity + scores.d1_quality) / 2
        : null,
      weight: w.d1, color: '#64748B', icon: '◈',
      status: (scores.d1_productivity != null && scores.d1_quality != null) ? 'OK' : 'MISSING',
      statusLabel: (scores.d1_productivity != null && scores.d1_quality != null) ? 'Stabil' : 'HRIS ausstehend',
      details: [
        { label: 'Auslastungsrate', value: null, unit: '%' },
        { label: 'Neukundenrate',   value: null, unit: '%' },
        { label: 'Pipeline-Input',  value: null, unit: ' Pts' },
      ],
      note: (scores.d1_productivity != null && scores.d1_quality != null) ? null : 'D1 wird verfügbar sobald HRIS-Integration aktiv ist.',
      coverage_badge: null,
    },
    (() => {
      const d2Score = scores.d2_rampup != null
        ? parseFloat(scores.d2_rampup)
        : (scores.d2_0_score != null ? parseFloat(scores.d2_0_score) : null);
      const d2FromSurvey = scores.d2_rampup == null && d2Score != null;
      return {
      id: 'D2', label: 'Ramp-Up', sublabel: '& Lernen',
      score: d2Score,
      weight: w.d2,
      color: '#F59E0B', icon: '⟳',
      status: d2Score != null ? 'PARTIAL' : 'MISSING',
      statusLabel: d2Score != null ? (d2FromSurvey ? 'Aus Kulturumfrage' : 'Teilweise') : 'Keine Daten',
      details: [
        { label: 'Ramp-Up Score',   value: scores.d2_rampup?.toFixed(0) ?? null, unit: ' Pts' },
        { label: 'Survey-Score',    value: d2FromSurvey ? d2Score.toFixed(0) : null, unit: ' Pts' },
        { label: 'Lernziel-Score',  value: null, unit: '' },
      ],
      note: d2FromSurvey
        ? 'Score basiert auf Kulturumfrage-Daten (Ramp-Up nicht verfügbar).'
        : 'd2_learning folgt in V2 (Kompetenz-Zeitreihe erforderlich).',
      d2FromSurvey,
      coverage_badge: coverage && coverage.type !== 'survey' ? `${coverage.covered ?? coverage.program_participants} / ${coverage.total_employees} Mitarbeiter` : null,
      coverage_status: coverage?.status ?? coverage?.coverage_status ?? null,
    }; })(),
    {
      id: 'D3', label: 'Retention', sublabel: '& Risiko',
      score: d3Score,
      weight: w.d3,
      color: '#10B981', icon: '⬡',
      status: d3Status, statusLabel: d3StatusLabel,
      details: [
        { label: 'Retention Rate',  value: scores.d3_retention?.toFixed(0) ?? null, unit: '%' },
        { label: 'Risk Exposure',   value: scores.d3_risk?.toFixed(0) ?? null, unit: ' Pts' },
        { label: 'HIGH Risk',       value: null, unit: ' User' },
      ],
      note: 'Fluktuationsrisiko-Monitoring aktiv.',
      coverage_badge: coverage && coverage.type !== 'survey' ? `${coverage.covered ?? coverage.program_participants} / ${coverage.total_employees} Mitarbeiter` : null,
      coverage_status: coverage?.status ?? coverage?.coverage_status ?? null,
    },
    {
      id: 'D4', label: 'Niederlassungs-', sublabel: 'kultur',
      score: d4Score,
      weight: w.d4,
      color: '#3B82F6', icon: '◎',
      status: d4Status,
      statusLabel: d4StatusLabel,
      subtitle: surveyAvailable
        ? `Anonyme Kulturumfrage, N=${d4Respondents}`
        : null,
      details: [
        { label: 'Kultur-Score',     value: d4Score?.toFixed(0) ?? null, unit: ' Pts' },
        { label: 'Teilnehmer',      value: surveyAvailable ? String(d4Respondents) : null, unit: '' },
        { label: 'Alerts aktiv',    value: String(alerts?.filter(a => a.alert_active).length ?? 0), unit: '' },
      ],
      note: surveyAvailable
        ? `Basiert auf anonymer Kulturumfrage, N=${d4Respondents}.`
        : 'Noch nicht genügend Antworten für diese Periode.',
      coverage_badge: coverage?.type === 'survey' && surveyAvailable
        ? `${d4Respondents} Antworten${['9100', '9102'].includes(String(tenant)) ? ' (modelliert)' : ''}`
        : null,
      coverage_status: coverage?.type === 'survey' ? (coverage?.status ?? coverage?.coverage_status ?? null) : null,
    },
  ];
}

/* ─── Markdown → HTML helper ─────────────────────────────────────── */
function mdToHtml(md) {
  return md
    .replace(/### (.*)/g, '<h3>$1</h3>')
    .replace(/## (.*)/g, '<h2>$1</h2>')
    .replace(/# (.*)/g, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n- (.*)/g, '\n<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)(?=\s*(?:[^<]|<(?!li)))/g, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
}

/* ─── ConversationGuideModal ─────────────────────────────────────── */
function ConversationGuideModal({ guide, actionTitle, onClose }) {
  const html = mdToHtml(guide);

  function handleExportPDF() {
    const now = new Date();
    const dateStr = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const title = `Gesprächsleitfaden: ${actionTitle || 'Massnahme'}`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(
      '<!DOCTYPE html><html><head><title>' + title.replace(/</g, '&lt;') + '</title>' +
      '<style>' +
      '@page { margin: 2cm; }' +
      'body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }' +
      'h1 { font-size: 20px; color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px; }' +
      'h2 { font-size: 16px; color: #1e3a5f; margin-top: 24px; }' +
      'h3 { font-size: 14px; color: #2d5f8a; margin-top: 20px; }' +
      'p { margin: 8px 0; }' +
      'ul { margin: 8px 0; padding-left: 24px; }' +
      'li { margin: 4px 0; }' +
      'strong { color: #1e3a5f; }' +
      '.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #ddd; }' +
      '.header-title { font-size: 22px; font-weight: bold; color: #1e3a5f; }' +
      '.header-meta { font-size: 12px; color: #666; text-align: right; }' +
      '.footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }' +
      '</style></head><body>' +
      '<div class="header"><div class="header-title">MENVIO Performance Index</div>' +
      '<div class="header-meta">' + title.replace(/</g, '&lt;') + '<br>' + dateStr + '</div></div>' +
      '<p>' + html + '</p>' +
      '<div class="footer">MENVIO GmbH \u2014 Performance ist kein Bauchgef\u00fchl. Sie ist messbar. \u2014 www.menvio.ai</div>' +
      '<script>window.onload = function() { window.print(); }<\/script>' +
      '</body></html>'
    );
    printWindow.document.close();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'linear-gradient(160deg, #111827 0%, #0b0f18 100%)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 22, padding: '28px 32px',
        maxWidth: 700, width: '100%', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Modal header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 3 }}>
              Gesprächsleitfaden
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
              {actionTitle}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8, color: 'rgba(255,255,255,0.45)', cursor: 'pointer',
            padding: '6px 12px', fontSize: 11,
          }}>✕ Schließen</button>
        </div>
        {/* Scrollable content */}
        <div style={{
          flex: 1, overflowY: 'auto', marginBottom: 18,
          fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.75,
        }}
          dangerouslySetInnerHTML={{ __html: '<p>' + html + '</p>' }}
        />
        {/* Footer with PDF button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={handleExportPDF} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 18px', fontSize: 12, fontWeight: 600,
            color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
            cursor: 'pointer', transition: 'background 0.15s',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Als PDF exportieren
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Deadline badge helper ──────────────────────────────────────── */
function deadlineBadge(dueDateStr) {
  const daysLeft = dueDateStr ? Math.ceil((new Date(dueDateStr) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  let bg, color, text;
  if (daysLeft === null) {
    bg = 'rgba(255,255,255,0.06)'; color = 'rgba(255,255,255,0.35)'; text = 'Keine Frist';
  } else if (daysLeft < 0) {
    bg = '#EF444420'; color = '#EF4444'; text = `${Math.abs(daysLeft)} Tage überfällig`;
  } else if (daysLeft === 0) {
    bg = '#EF444420'; color = '#EF4444'; text = 'Heute fällig';
  } else if (daysLeft <= 14) {
    bg = '#F59E0B20'; color = '#F59E0B'; text = `noch ${daysLeft} Tage`;
  } else {
    bg = '#10B98120'; color = '#10B981'; text = `noch ${daysLeft} Tage`;
  }
  return { bg, color, text, daysLeft };
}

/* ─── ActionsPanel ───────────────────────────────────────────────── */
const STATUS_CONFIG = {
  active:    { dot: '#10B981', label: 'Aktiv',         bg: '#10B98112', border: '#10B98140' },
  completed: { dot: '#3B82F6', label: 'Abgeschlossen', bg: '#3B82F612', border: '#3B82F640' },
  dismissed: { dot: '#64748B', label: 'Verworfen',     bg: '#47556912', border: '#47556940' },
};

function ActionsPanel({ nlActions, rlActions, actionsLoading, onOpenChat, tenantParam }) {
  const [expanded, setExpanded] = useState(null);
  const [prepareLoading, setPrepareLoading] = useState(null);
  const [guideData, setGuideData] = useState(null); // { title, text }
  const maxSlots = 3;
  const isRL = !!tenantParam; // RL views NL dashboard via ?tenant=

  // Own actions: NL sees nl_actions, RL sees rl_actions
  const myActions = isRL ? rlActions : nlActions;
  const myActiveCount = myActions.filter(a => a.status === 'active').length;

  async function handlePrepareConversation(action) {
    setPrepareLoading(action.id);
    try {
      const tq = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}` : '';
      const res = await axios.post(`/mpi/ai/prepare-conversation${tq}`, { action_id: action.id }, { withCredentials: true, timeout: 60000 });
      if (res.data) {
        const gd = { title: action.title, text: res.data.guide || res.data.text || res.data.antwort || JSON.stringify(res.data) };
        setGuideData(gd);
      }
    } catch (err) {
      console.error('prepare-conversation error:', err);
    }
    setPrepareLoading(null);
  }

  if (actionsLoading) {
    return (
      <div style={{
        maxWidth: 1000, margin: '24px auto 0',
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18, padding: '20px 24px',
      }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em' }}>
          Massnahmen laden …
        </div>
      </div>
    );
  }

  /* Shared: compact read-only table */
  function renderCompactTable(items) {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {['Maßnahme', 'Dimension', 'Frist', 'Status'].map(h => (
              <th key={h} style={{
                textAlign: 'left', padding: '8px 10px', fontSize: 9,
                color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', fontWeight: 600,
                textTransform: 'uppercase',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(action => {
            const sc = STATUS_CONFIG[action.status] || STATUS_CONFIG.active;
            const dl = deadlineBadge(action.due_date || action.deadline);
            const dueDateObj = (action.due_date || action.deadline) ? new Date(action.due_date || action.deadline) : null;
            const fristText = dueDateObj
              ? dueDateObj.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : 'Keine Frist';
            return (
              <tr key={action.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.1s' }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ padding: '10px 10px', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                  {action.title}
                </td>
                <td style={{ padding: '10px 10px' }}>
                  {action.dimension ? (
                    <span style={{
                      fontSize: 9, fontWeight: 600, letterSpacing: '0.06em',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8, padding: '2px 8px', color: 'rgba(255,255,255,0.45)',
                    }}>{action.dimension}</span>
                  ) : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
                </td>
                <td style={{ padding: '10px 10px', color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
                  {fristText}
                </td>
                <td style={{ padding: '10px 10px' }}>
                  {action.status === 'active' ? (
                    <span style={{
                      fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap',
                      background: dl.bg, border: `1px solid ${dl.color}40`,
                      borderRadius: 8, padding: '2px 8px', color: dl.color,
                    }}>{dl.text}</span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 10, fontWeight: 600,
                      background: sc.bg, border: `1px solid ${sc.border}`,
                      borderRadius: 10, padding: '2px 9px', color: sc.dot,
                    }}>
                      {action.status === 'completed'
                        ? <span style={{ fontSize: 10 }}>✓</span>
                        : <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />}
                      {sc.label}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  /* Shared: accordion cards with buttons */
  function renderAccordion(items, keyPrefix) {
    return items.map((action, idx) => {
      const sc = STATUS_CONFIG[action.status] || STATUS_CONFIG.active;
      const expandKey = keyPrefix + idx;
      const isExpanded = expanded === expandKey;
      const dl = deadlineBadge(action.due_date || action.deadline);
      const isActive = action.status === 'active';
      return (
        <div key={action.id || idx} style={{
          background: isExpanded ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${isExpanded ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)'}`,
          borderRadius: 14, padding: '14px 18px',
          marginBottom: idx < items.length - 1 ? 10 : 0,
          cursor: 'pointer', transition: 'all 0.15s ease',
        }}
          onClick={() => setExpanded(isExpanded ? null : expandKey)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
              <span style={{
                fontSize: 11, color: isExpanded ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
                transition: 'transform 0.15s ease', display: 'inline-block',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}>▶</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {action.title}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {action.dimension && (
                <span style={{
                  fontSize: 9, fontWeight: 600, letterSpacing: '0.06em',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '2px 8px', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap',
                }}>{action.dimension}</span>
              )}
              <span style={{
                display: 'flex', alignItems: 'center', gap: 5,
                fontSize: 10, fontWeight: 600,
                background: sc.bg, border: `1px solid ${sc.border}`,
                borderRadius: 10, padding: '2px 9px', color: sc.dot, whiteSpace: 'nowrap',
              }}>
                {action.status === 'completed'
                  ? <span style={{ fontSize: 10 }}>✓</span>
                  : <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />}
                {sc.label}
              </span>
              {isActive && (
                <span style={{
                  fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap',
                  background: dl.bg, border: `1px solid ${dl.color}40`,
                  borderRadius: 8, padding: '2px 8px', color: dl.color,
                }}>{dl.text}</span>
              )}
            </div>
          </div>
          {isExpanded && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {action.description && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, whiteSpace: 'pre-line', marginBottom: 12 }}>
                  {action.description}
                </div>
              )}
              {action.status === 'active' && (
                <button
                  onClick={e => { e.stopPropagation(); handlePrepareConversation(action); }}
                  disabled={prepareLoading != null}
                  style={{
                    padding: '6px 14px', fontSize: 11, fontWeight: 600, color: '#9AF68C',
                    background: prepareLoading === action.id ? 'rgba(154,246,140,0.12)' : 'rgba(154,246,140,0.06)',
                    border: '1px solid rgba(154,246,140,0.25)', borderRadius: 9,
                    cursor: prepareLoading != null ? 'default' : 'pointer', transition: 'background 0.15s',
                    opacity: prepareLoading != null && prepareLoading !== action.id ? 0.4 : 1,
                  }}
                >{prepareLoading === action.id ? 'Leitfaden wird erstellt …' : 'Gespräch vorbereiten'}</button>
              )}
            </div>
          )}
        </div>
      );
    });
  }

  /* Section header helper */
  function renderSectionHeader(title, activeCount_, showSlots) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.88)' }}>
            {title}
          </span>
          {showSlots && (
            <span style={{
              fontSize: 10, fontWeight: 600,
              background: activeCount_ > 0 ? '#10B98118' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${activeCount_ > 0 ? '#10B98140' : 'rgba(255,255,255,0.1)'}`,
              color: activeCount_ > 0 ? '#10B981' : 'rgba(255,255,255,0.35)',
              borderRadius: 12, padding: '2px 10px',
            }}>
              {activeCount_}/{maxSlots}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 1000, margin: '24px auto 0',
      background: 'rgba(255,255,255,0.03)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 18, padding: '20px 24px',
    }}>
      {/* ── NL-Manager view: own actions as accordion ── */}
      {!isRL && (
        <>
          {renderSectionHeader('Aktive Maßnahmen', myActiveCount, true)}
          {nlActions.length === 0 && (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
                Noch keine Maßnahmen definiert. Nutze den KI-Assistenten um<br />basierend auf deinen MPI-Daten konkrete Maßnahmen zu erstellen.
              </div>
              <button onClick={onOpenChat} style={{
                marginTop: 10, padding: '7px 16px',
                background: 'linear-gradient(90deg, #9AF68C, #7EE87E)',
                border: 'none', borderRadius: 9,
                color: '#0b0f0a', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', letterSpacing: '0.03em',
              }}>KI-Assistent öffnen</button>
            </div>
          )}
          {nlActions.length > 0 && renderAccordion(nlActions, 'nl-')}
        </>
      )}

      {/* ── RL view: Sektion 1 – NL-Massnahmen (read-only table) ── */}
      {isRL && (
        <>
          {renderSectionHeader('NL-Maßnahmen', nlActions.filter(a => a.status === 'active').length, false)}
          {nlActions.length === 0 ? (
            <div style={{ padding: '12px 0 20px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              Keine Maßnahmen definiert für diese Niederlassung.
            </div>
          ) : renderCompactTable(nlActions)}

          {/* Sektion 2 – Eigene RL-Massnahmen (accordion with buttons) */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {renderSectionHeader('Eigene Maßnahmen', myActiveCount, true)}
            {rlActions.length === 0 ? (
              <div style={{ padding: '12px 0', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                Noch keine eigenen Maßnahmen definiert.
              </div>
            ) : renderAccordion(rlActions, 'rl-')}
          </div>
        </>
      )}

      {guideData && ReactDOM.createPortal(
        <ConversationGuideModal
          guide={guideData.text}
          actionTitle={guideData.title}
          onClose={() => setGuideData(null)}
        />,
        document.body
      )}
    </div>
  );
}

/* Kumulierter Vier-Quartals-Trend (rein deskriptiv, kein Score). */
function cumulativeTrend(trendSeries) {
  const pts = (Array.isArray(trendSeries) ? trendSeries : [])
    .filter(p => p && p.mpi_total != null)
    .map(p => Number(p.mpi_total));
  if (pts.length < 2) return { value: 'Kein Vergleich', color: '#9CA3AF' };
  const delta = pts[pts.length - 1] - pts[0];
  if (delta > 2)   return { value: 'Steigend',       color: '#10B981' };
  if (delta >= -2) return { value: 'Stabil',         color: '#10B981' };
  if (delta >= -5) return { value: 'Leicht fallend', color: '#F59E0B' };
  if (delta >= -9) return { value: 'Fallend',        color: '#F97316' };
  return                  { value: 'Stark fallend',  color: '#EF4444' };
}

/* Persistenz: nur bei streng monotoner Reihe (>=2 Punkte). N = Perioden. */
function trendPersistence(trendSeries) {
  const pts = (Array.isArray(trendSeries) ? trendSeries : [])
    .filter(p => p && p.mpi_total != null)
    .map(p => Number(p.mpi_total));
  if (pts.length < 2) return null;
  let allDown = true, allUp = true;
  for (let i = 1; i < pts.length; i++) {
    const d = pts[i] - pts[i - 1];
    if (d >= 0) allDown = false;
    if (d <= 0) allUp = false;
  }
  if (allDown) return `seit ${pts.length} Quartalen fallend`;
  if (allUp)   return `seit ${pts.length} Quartalen steigend`;
  return null;
}

/* ─── Gauge Summary Builder ──────────────────────────────────────── */
function buildGaugeSummary(current, previous, scoresRaw, riskHighCount, datasourceActiveCount, trendSeries, d3SurveyRisk) {
  // --- Signal 1: kumulierter Vier-Quartals-Trend (erster -> letzter gueltiger mpi_total) ---
  const { value: trendValue, color: trendColor } = cumulativeTrend(trendSeries);

  // --- Signal 2: Risiko-MA (Anzahl HIGH-Risk-Mitarbeiter) ---
  let riskValue, riskColor;
  if (riskHighCount != null) {
    if (riskHighCount === 0)     { riskValue = '0 HIGH';                riskColor = '#10B981'; }
    else if (riskHighCount <= 2) { riskValue = `${riskHighCount} HIGH`; riskColor = '#F59E0B'; }
    else                         { riskValue = `${riskHighCount} HIGH`; riskColor = '#EF4444'; }
  } else if (d3SurveyRisk != null) {
    // Survey-Fallback (kein Mentoring-Risikomodell): Stufe aus d3_survey_score.
    // Farbe via dimColor -> identisch zur D3-Dimensionskachel derselben NL.
    riskColor = dimColor(d3SurveyRisk);
    riskValue = d3SurveyRisk >= 70 ? 'Niedrig' : d3SurveyRisk >= 55 ? 'Erhöht' : 'Hoch';
  } else {
    riskValue = '---'; riskColor = '#9CA3AF';
  }

  // --- Signal 3: Datenbasis (aktive Datenquellen von 5) ---
  let dataValue, dataColor;
  const activeCount = datasourceActiveCount;
  if (activeCount != null) {
    if (activeCount >= 4)      { dataValue = 'Stark';                dataColor = '#10B981'; }
    else if (activeCount >= 2) { dataValue = `${activeCount} von 5`; dataColor = '#F59E0B'; }
    else                       { dataValue = 'Dünn';                 dataColor = '#EF4444'; }
  } else {
    dataValue = '---'; dataColor = '#9CA3AF';
  }

  return [
    { label: 'Trend',      value: trendValue, color: trendColor },
    { label: 'Risiko-MA',  value: riskValue,  color: riskColor  },
    { label: 'Datenbasis', value: dataValue,  color: dataColor  },
  ];
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function MPIDashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantParam = searchParams.get('tenant');

  const [scores, setScores]         = useState(null);
  const [prevScores, setPrevScores] = useState(null);
  const [scoresRaw, setScoresRaw]   = useState(null);
  const [trendSeries, setTrendSeries] = useState(null);
  const [d3SurveyRisk, setD3SurveyRisk] = useState(null);
  const [nlActions, setNlActions]   = useState([]);
  const [rlActions, setRlActions]   = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [showD1Drilldown, setShowD1Drilldown] = useState(false);
  const [showD2Drilldown, setShowD2Drilldown] = useState(false);
  const [showD3Drilldown, setShowD3Drilldown] = useState(false);
  const [showD4Drilldown, setShowD4Drilldown] = useState(false);
  const [showTrend, setShowTrend] = useState(false);
  const [alerts, setAlerts]         = useState([]);
  const [leadershipSurvey, setLeadershipSurvey] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [tenant, setTenant]     = useState('');
  const [period, setPeriod]     = useState('');
  const [prevMpi, setPrevMpi]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [active, setActive]     = useState(null);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [datasource, setDatasource] = useState(null);
  const [riskHighCount, setRiskHighCount] = useState(null);

  // Fetch HIGH-Risk count for Gauge-Summary "Risiko-MA" signal.
  // scoresRaw liefert kein risk_summary → separater Call zu /mpi/risk-details.
  useEffect(() => {
    let alive = true;
    const tq = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}` : '';
    axios.get(`/mpi/risk-details${tq}`, { withCredentials: true })
      .then(res => {
        if (!alive) return;
        setRiskHighCount(res.data?.distribution?.high?.count ?? null);
        setD3SurveyRisk(res.data?.d3_survey_score != null ? Number(res.data.d3_survey_score) : null);
      })
      .catch(() => { if (alive) { setRiskHighCount(null); setD3SurveyRisk(null); } });
    return () => { alive = false; };
  }, [tenantParam]);

  // Vier-Quartals-Reihe fuer das Trend-Label (rein deskriptiv; .catch -> null).
  useEffect(() => {
    let alive = true;
    const qs = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}&periods=4` : '?periods=4';
    axios.get(`/mpi/scores/trend${qs}`, { withCredentials: true })
      .then(r => { if (alive) setTrendSeries(Array.isArray(r.data) ? r.data : null); })
      .catch(() => { if (alive) setTrendSeries(null); });
    return () => { alive = false; };
  }, [tenantParam]);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const whoami = await ensureMentoringSession();
        const wu = whoami?.user || whoami;
        if (alive && wu) {
          setUserName(wu.first_name || wu.firstName || wu.firstname || '');
          setUserRole(wu.role || wu.mentoring_role || '');
        }
        const tq = tenantParam ? `?tenant=${encodeURIComponent(tenantParam)}` : '';
        const [scoresRes, alertsRes, surveyRes] = await Promise.all([
          axios.get(`/mpi/scores${tq}`, { withCredentials: true }),
          axios.get(`/mpi/alerts${tq}`, { withCredentials: true }),
          axios.get(`/mpi/leadership-survey${tq}`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setScoresRaw(scoresRes.data ?? null);
        setScores(scoresRes.data?.current ?? scoresRes.data);
        setPrevScores(scoresRes.data?.previous ?? null);
        setPrevMpi(scoresRes.data?.previous?.mpi_total ?? null);
        setTenant(scoresRes.data?.tenant_name ?? '');
        setPeriod(scoresRes.data?.period ?? '');
        setAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
        setLeadershipSurvey(surveyRes.data ?? null);
        setCoverage(scoresRes.data?.coverage || null);
        // Load actions separately (non-blocking)
        try {
          const actionsRes = await axios.get(`/mpi/actions${tq}`, { withCredentials: true });
          if (alive) {
            setNlActions(Array.isArray(actionsRes.data?.nl_actions) ? actionsRes.data.nl_actions : []);
            setRlActions(Array.isArray(actionsRes.data?.rl_actions) ? actionsRes.data.rl_actions : []);
          }
        } catch {
          if (alive) { setNlActions([]); setRlActions([]); }
        } finally {
          if (alive) setActionsLoading(false);
        }
        // Load datasource status (non-blocking)
        try {
          const dsRes = await axios.get(`/mpi/datasource-status${tq}`, { withCredentials: true });
          if (alive) setDatasource(dsRes.data ?? null);
        } catch {
          if (alive) setDatasource(null);
        }

      } catch (e) {
        if (DEV) console.error('[MPI] load error:', e);
        if (alive) setError('Daten konnten nicht geladen werden.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [tenantParam]);

  const handle = (dim) => setActive(p => p?.id === dim.id ? null : dim);

  const handleLogout = () => {
    window.location.replace('/#/nl-login');
    fetch('/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  };

  /* ── Loading / Error states ── */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#080b12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em' }}>LADE MPI DATEN …</div>
      </div>
    );
  }

  if (error || !scores) {
    return (
      <div style={{ minHeight: '100vh', background: '#080b12', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div style={{ fontSize: 13, color: '#EF4444' }}>{error || 'Keine Daten verfügbar.'}</div>
        <button onClick={() => navigate(-1)} style={{
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: '8px 16px', fontSize: 12,
        }}>← Zurück</button>
      </div>
    );
  }

  // A: NULL-Diskriminator statt ?? 0 -> Ring zeigt "keine Daten" statt rote 0.
  const mpiIsNull = scores.mpi_total == null;
  const mpi = mpiIsNull ? null : scores.mpi_total;
  const prev = prevMpi == null ? null : prevMpi;            // kein Fallback auf mpi -> kein kuenstliches 0.0-Delta
  const trend = (mpi != null && prev != null) ? mpi - prev : null;
  // B: Survey-only-Hinweis nur wenn wirklich genau die Survey-Quelle aktiv ist (>=1, nicht <=1).
  const dsActiveSegs = (datasource?.segments || []).filter(s => s && s.active);
  const isSurveyOnly = (datasource?.active_count ?? 0) >= 1 && (
    dsActiveSegs.length > 0
      ? (dsActiveSegs.length === 1 && /survey|kultur|umfrage|s1/i.test(String(dsActiveSegs[0].id || dsActiveSegs[0].key || dsActiveSegs[0].name || dsActiveSegs[0].label || '')))
      : (datasource?.active_count === 1)
  );
  const dims = buildDimensions(scores, alerts, leadershipSurvey, coverage, tenantParam);
  const activeAlerts = alerts.filter(a => a.alert_active);
  const [D1, D2, D3, D4] = dims;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 0%, #0d1520 0%, #080b12 45%, #050709 100%)',
      fontFamily: "-apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
      color: 'white',
      padding: '28px 32px 40px',
    }}>
      <style>{`
        @keyframes mpiSlideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes mpiPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        * { box-sizing:border-box; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => tenantParam ? navigate('/regional-dashboard') : navigate(-1)} style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 9, color: 'rgba(255,255,255,0.45)', cursor: 'pointer',
            padding: '7px 13px', fontSize: 11,
          }}>{tenantParam ? '← Zurück zur Region' : '← Zurück'}</button>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, boxShadow: '0 4px 14px #3B82F644',
          }}>◎</div>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.28em', textTransform: 'uppercase' }}>
              MENVIO Performance Index
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px' }}>
              {tenant || 'Performance Dashboard'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {activeAlerts.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#EF444410', border: '1px solid #EF444430',
              borderRadius: 9, padding: '7px 13px',
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF4444', animation: 'mpiPulse 2s infinite' }} />
              <span style={{ fontSize: 11, color: '#EF4444' }}>{activeAlerts.length} Alert{activeAlerts.length > 1 ? 's' : ''} aktiv</span>
            </div>
          )}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 9, padding: '7px 13px',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 7px #10B981' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{period}</span>
          </div>
          {['nl_manager', 'hr', 'hr_admin'].includes(userRole) && !tenantParam && (
            <button
              onClick={() => navigate('/invite-employees')}
              style={{
                padding: '7px 14px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 9,
                color: 'rgba(255,255,255,0.6)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                letterSpacing: '0.03em',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.5)'; e.currentTarget.style.color = 'rgba(245,158,11,0.9)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
            >
              Mitarbeiter einladen
            </button>
          )}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-support-chat', {
              detail: { role: userRole || 'nl_manager', firstName: userName, tenantName: tenant, tenantId: tenantParam || '' }
            }))}
            style={{
              padding: '7px 14px',
              background: 'linear-gradient(90deg, #9AF68C, #7EE87E)',
              border: 'none',
              borderRadius: 9,
              color: '#0b0f0a',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
          >
            KI-Assistent
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '7px 14px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 9,
              color: 'rgba(255,255,255,0.45)',
              fontSize: 11,
              cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
          >
            Abmelden
          </button>
        </div>
      </div>

      {/* ── DATASOURCE BAR ── */}
      {datasource && (
        <DataSourceBar
          segments={orderDatasourceSegments(datasource.segments)}
          activeCount={datasource.active_count}
          maxCount={datasource.max_count || datasource.total_count}
          isS1Only={isSurveyOnly}
        />
      )}

      {/* ── COCKPIT GRID ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 280px 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 18,
        maxWidth: 1000,
        margin: '0 auto',
        minHeight: 480,
      }}>
        {/* D1 top-left */}
        <div style={{ gridColumn: 1, gridRow: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DimCard dim={D1} onClick={() => setShowD1Drilldown(true)} active={active?.id === 'D1'} />
          <button
            onClick={() => setShowD1Drilldown(true)}
            style={{
              width: '100%',
              background: 'rgba(100,116,139,0.07)',
              border: '1px solid rgba(100,116,139,0.25)',
              borderRadius: 10,
              color: '#94A3B8',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '7px 12px',
              letterSpacing: '0.04em',
              transition: 'background 0.15s ease',
            }}
          >
            Produktivitäts-Details anzeigen ↗
          </button>
        </div>

        {/* CENTRAL MPI — spans both rows */}
        <div style={{
          gridColumn: 2, gridRow: '1 / 3',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 28, padding: '32px 22px 28px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.09)',
          backdropFilter: 'blur(20px)',
          position: 'relative', overflow: 'hidden', gap: 24,
        }}>
          <div style={{
            position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
            width: 260, height: 260, borderRadius: '50%',
            background: trend < 0
              ? 'radial-gradient(#F59E0B0A, transparent 70%)'
              : 'radial-gradient(#10B98110, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <CentralGauge score={mpi} prev={prev} />
          <button
            type="button"
            aria-label="Verlauf anzeigen"
            onClick={() => setShowTrend(true)}
            style={{
              marginTop: -10,
              background: 'rgba(46,117,182,0.10)',
              border: '1px solid rgba(46,117,182,0.35)',
              borderRadius: 8, color: '#8ab4e8',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              padding: '5px 12px', letterSpacing: '0.03em',
            }}
          >
            Verlauf anzeigen ↗
          </button>
          <div style={{
            width: '100%', borderTop: '1px solid rgba(255,255,255,0.07)',
            paddingTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          }}>
            {buildGaugeSummary(scores, prevScores, scoresRaw, riskHighCount, datasource?.active_count, trendSeries, d3SurveyRisk).map((s, i) => (
              <div key={i} style={{
                textAlign: 'center', padding: '0 8px',
                borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.09em', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
          {(() => {
            const p = trendPersistence(trendSeries);
            return p ? (
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', textAlign: 'center', marginTop: 6 }}>{p}</div>
            ) : null;
          })()}
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)', textAlign: 'center', lineHeight: 1.7 }}>
            {({
              'FULL':       'D1 40%  ·  D2 20%  ·  D3 20%  ·  D4 20%',
              'S1_S2':      'D1 40%  ·  D2 15%  ·  D3 20%  ·  D4 25%',
              'S1_ONLY':    'D2 15%  ·  D3 25%  ·  D4 60%',
              'D4_ONLY':    'D3 30%  ·  D4 70%',
              'D1_MISSING': 'D2 25%  ·  D3 37.5%  ·  D4 37.5%',
            })[scores.weighting_mode] || 'D1 40%  ·  D2 20%  ·  D3 20%  ·  D4 20%'}<br />
            {(scores.weighting_mode === 'D1_MISSING' || scores.weighting_mode === 'S1_ONLY' || scores.weighting_mode === 'D4_ONLY') && (
              <span style={{ color: 'rgba(255,255,255,0.1)' }}>D1 aktiv nach HRIS-Integration</span>
            )}
          </div>
          <p style={{ color: '#6b7280', fontSize: '11px', textAlign: 'center', margin: 0, lineHeight: '1.4' }}>
            MENVIO zeigt Muster und Trends – keine individuelle Überwachung.<br />
            Scores werden auf Team- und Rollenebene aggregiert.
          </p>
          {coverage && (coverage.total_employees > 0 || coverage.covered > 0) && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              {((coverage.covered ?? coverage.program_participants) == null || mpiIsNull) ? (
                // C: keine echten Antwortdaten ueber Schwelle -> nicht "0 von X", sondern Hinweis.
                <span style={{ color: '#F59E0B', fontSize: '0.8rem', opacity: 0.95 }}>
                  {coverage.label || 'Survey-Teilnahme'}: unter Anonymitätsschwelle
                </span>
              ) : (
                <>
                  <span style={{
                    color: (coverage.status || coverage.coverage_status) === 'GREEN' ? '#22C55E' : (coverage.status || coverage.coverage_status) === 'YELLOW' ? '#F59E0B' : '#EF4444',
                    fontSize: '0.8rem',
                    opacity: 0.95,
                  }}>
                    {coverage.label || 'Abdeckung'}: {coverage.covered ?? coverage.program_participants ?? 0} von {coverage.total_employees || 0} Mitarbeitern ({coverage.pct ?? coverage.coverage_pct ?? 0}%){['9100', '9102'].includes(String(tenantParam)) ? ' (modelliert)' : ''}
                  </span>
                  {(coverage.status || coverage.coverage_status) === 'RED' && (
                    <div style={{ color: '#F59E0B', fontSize: '0.7rem', marginTop: 4 }}>
                      Eingeschränkte Aussagekraft. {coverage.label || 'Abdeckung'} erhöhen.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* D2 top-right */}
        <div style={{ gridColumn: 3, gridRow: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DimCard dim={D2} onClick={() => setShowD2Drilldown(true)} active={active?.id === 'D2'} />
          <button
            onClick={() => setShowD2Drilldown(true)}
            style={{
              width: '100%',
              background: 'rgba(245,158,11,0.07)',
              border: '1px solid rgba(245,158,11,0.25)',
              borderRadius: 10,
              color: '#F59E0B',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '7px 12px',
              letterSpacing: '0.04em',
              transition: 'background 0.15s ease',
            }}
          >
            Ramp-Up Details anzeigen ↗
          </button>
        </div>

        {/* D3 bottom-left */}
        <div style={{ gridColumn: 1, gridRow: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DimCard dim={D3} onClick={() => setShowD3Drilldown(true)} active={active?.id === 'D3'} />
          <button
            onClick={() => setShowD3Drilldown(true)}
            style={{
              width: '100%',
              background: 'rgba(16,185,129,0.07)',
              border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 10,
              color: '#10B981',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '7px 12px',
              letterSpacing: '0.04em',
              transition: 'background 0.15s ease',
            }}
          >
            Risiko-Details anzeigen ↗
          </button>
        </div>

        {/* D4 bottom-right */}
        <div style={{ gridColumn: 3, gridRow: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <DimCard dim={D4} onClick={() => setShowD4Drilldown(true)} active={active?.id === 'D4'} />
          <button
            onClick={() => setShowD4Drilldown(true)}
            style={{
              width: '100%',
              background: 'rgba(59,130,246,0.07)',
              border: '1px solid rgba(59,130,246,0.25)',
              borderRadius: 10,
              color: '#3B82F6',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '7px 12px',
              letterSpacing: '0.04em',
              transition: 'background 0.15s ease',
            }}
          >
            Kultur-Details anzeigen ↗
          </button>
        </div>
      </div>

      {/* Detail drawer */}
      {active && (
        <div style={{ maxWidth: 1000, margin: '16px auto 0' }}>
          <DetailDrawer dim={active} onClose={() => setActive(null)} />
        </div>
      )}

      {/* Actions panel */}
      <ActionsPanel
        nlActions={nlActions}
        rlActions={rlActions}
        actionsLoading={actionsLoading}
        tenantParam={tenantParam}
        onOpenChat={() => window.dispatchEvent(new CustomEvent('open-support-chat', {
          detail: { role: userRole || 'nl_manager', firstName: userName, tenantName: tenant, tenantId: tenantParam || '' }
        }))}
      />

      {/* Alert banners — culture_alert first, then mentoring_quality */}
      {(() => {
        const cultureAlerts = activeAlerts.filter(a => a.alert_type === 'culture_alert');
        const mqAlerts = activeAlerts.filter(a => a.alert_type !== 'culture_alert');
        return (
          <>
            {coverage && (coverage.pct ?? coverage.coverage_pct) < coverage.target_pct && ((coverage.status || coverage.coverage_status) === 'YELLOW' || (coverage.status || coverage.coverage_status) === 'RED') && (
              <div style={{
                maxWidth: 1000, margin: '16px auto 0',
                background: coverage.type === 'survey' && (coverage.pct ?? coverage.coverage_pct) > 0
                  ? 'linear-gradient(90deg, #F59E0B0E, #F59E0B06, transparent)'
                  : `linear-gradient(90deg, ${(coverage.status || coverage.coverage_status) === 'RED' ? '#EF44440E' : '#F59E0B0E'}, ${(coverage.status || coverage.coverage_status) === 'RED' ? '#EF444406' : '#F59E0B06'}, transparent)`,
                border: `1px solid ${coverage.type === 'survey' && (coverage.pct ?? coverage.coverage_pct) > 0 ? '#F59E0B20' : (coverage.status || coverage.coverage_status) === 'RED' ? '#EF444420' : '#F59E0B20'}`,
                borderRadius: 12, padding: '12px 18px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: coverage.type === 'survey' && (coverage.pct ?? coverage.coverage_pct) > 0 ? '#F59E0B' : (coverage.status || coverage.coverage_status) === 'RED' ? '#EF4444' : '#F59E0B', animation: 'mpiPulse 2s infinite', flexShrink: 0 }} />
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                  <span style={{ color: coverage.type === 'survey' && (coverage.pct ?? coverage.coverage_pct) > 0 ? '#F59E0B' : (coverage.status || coverage.coverage_status) === 'RED' ? '#EF4444' : '#F59E0B', fontWeight: 600 }}>{coverage.label || 'Abdeckung'} · </span>
                  {coverage.label || 'Programm-Abdeckung'} unter Ziel: {coverage.pct ?? coverage.coverage_pct}% (Ziel: {coverage.target_pct}%)
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                  {period}
                </div>
              </div>
            )}
            {cultureAlerts.length > 0 && (
              <div style={{
                maxWidth: 1000, margin: '16px auto 0',
                background: 'linear-gradient(90deg, #3B82F60E, #3B82F606, transparent)',
                border: '1px solid #3B82F620',
                borderRadius: 12, padding: '12px 18px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#3B82F6', animation: 'mpiPulse 2s infinite', flexShrink: 0 }} />
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                  <span style={{ color: '#3B82F6', fontWeight: 600 }}>Kultur-Alert · D4 </span>
                  Niederlassungskultur unter Schwellenwert
                  {leadershipSurvey?.d4_survey_score != null && ` (Score: ${parseFloat(leadershipSurvey.d4_survey_score).toFixed(1)})`}
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                  {period}
                </div>
              </div>
            )}
            {mqAlerts.length > 0 && (
              <div style={{
                maxWidth: 1000, margin: '16px auto 0',
                background: 'linear-gradient(90deg, #EF44440E, #EF444406, transparent)',
                border: '1px solid #EF444420',
                borderRadius: 12, padding: '12px 18px',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#EF4444', animation: 'mpiPulse 2s infinite', flexShrink: 0 }} />
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                  <span style={{ color: '#EF4444', fontWeight: 600 }}>Leadership Alert · D4 </span>
                  {mqAlerts.length}{' '}{mqAlerts.length === 1 ? 'Führungskraft' : 'Führungskräfte'} unter Peer-Ø {mqAlerts[0]?.peer_avg_le?.toFixed(1)} — Trend fallend
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                  {period}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {showD1Drilldown && <D1ProductivityDrillDown current={scores} previous={prevScores} period={period} onClose={() => setShowD1Drilldown(false)} />}
      {showD2Drilldown && <D2RampupDrillDown period={period} tenant={tenantParam} d2Score={D2.score} surveyMode={D2.d2FromSurvey === true || coverage?.type === 'survey'} funktionSplit={scoresRaw?.funktion_split_d2} onClose={() => setShowD2Drilldown(false)} />}
      {showD3Drilldown && <D3RiskDrillDown period={period} tenant={tenantParam} onClose={() => setShowD3Drilldown(false)} />}
      {showD4Drilldown && <D4LeadershipDrillDown current={scores} previous={prevScores} scoresRaw={scoresRaw} period={period} tenant={tenantParam} onClose={() => setShowD4Drilldown(false)} />}
      {showTrend && <MpiTrendModal tenant={tenantParam} period={period} onClose={() => setShowTrend(false)} />}
    </div>
  );
}